import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Divider } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { verifyMfaLogin } from '@/api/auth-flow';

interface Props {
  email: string;
  password: string;
  onBack: () => void;
}

export function TotpVerificationForm({ email, password, onBack }: Props) {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [useBackup, setUseBackup] = useState(false);
  const [otp, setOtp] = useState('');
  const [backup, setBackup] = useState('');

  const { mutateAsync, isPending } = useMutation({ mutationFn: verifyMfaLogin });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await mutateAsync({
        email,
        password,
        ...(useBackup ? { backup_code: backup } : { otp_code: otp }),
      });
      if (res.errors?.length) {
        setError(res.errors[0].message);
        return;
      }
      if (setSession(res.data ?? {})) {
        navigate('/', { replace: true });
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    }
  };

  const disabled = isPending || (useBackup ? !backup : otp.length !== 6);

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {error ? <Alert tone="danger" title={error} /> : null}

      {useBackup ? (
        <Field label="Backup code" required>
          {(p) => (
            <Input
              {...p}
              value={backup}
              onChange={(e) => setBackup(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              autoComplete="one-time-code"
            />
          )}
        </Field>
      ) : (
        <Field label="Authentication code" required description="6-digit code from your authenticator app.">
          {(p) => (
            <Input
              {...p}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              autoComplete="one-time-code"
            />
          )}
        </Field>
      )}

      <div style={{ textAlign: 'right', fontSize: 13 }}>
        <button
          type="button"
          onClick={() => {
            setUseBackup((v) => !v);
            setError(null);
          }}
          style={{
            background: 'none',
            border: 0,
            cursor: 'pointer',
            color: 'var(--vn-primary-solid)',
            textDecoration: 'underline',
          }}
        >
          {useBackup ? 'Use authenticator app' : 'Use a backup code'}
        </button>
      </div>

      <Button type="submit" variant="primary" fullWidth disabled={disabled} loading={isPending}>
        Verify
      </Button>

      <Divider>or</Divider>

      <Button type="button" variant="secondary" fullWidth onClick={onBack}>
        Back to login
      </Button>
    </form>
  );
}
